import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Camera, Paperclip, Printer, Receipt, X } from 'lucide-react';
import {
  approveClaim,
  canApproveClaims,
  getClaims,
  getClaimsStats,
  rejectClaim,
  resubmitClaim,
  submitClaim,
  uploadScanFile,
  type ClaimItem,
  type ClaimResubmitFieldUpdates,
} from '../api/claims';
import {
  CLAIM_STATUS_LABELS,
  CLAIM_STATUS_VARIANT,
  type ClaimStatus,
} from '@hrmydeck/design-tokens/status';
import { useAuth } from '../store/auth';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import DocumentScanner from '../components/DocumentScanner';
import EmptyState from '../components/ui/EmptyState';
import LoadingState from '../components/ui/LoadingState';
import MetricCard from '../components/ui/MetricCard';
import PageHeader from '../components/ui/PageHeader';
import { formatDate, formatMoney } from '@/lib/format';
import { cn } from '@/lib/utils';
import { resolveApiOrigin } from '@/lib/app-config';

function storageUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  return `${resolveApiOrigin()}/storage/${path}`;
}

function isImagePath(path: string): boolean {
  try {
    const url = new URL(path);
    const privatePath = url.searchParams.get('path') ?? url.pathname;
    return /\.(jpe?g|png|gif|webp)$/i.test(privatePath);
  } catch {
    return /\.(jpe?g|png|gif|webp)$/i.test(path);
  }
}

const fieldClass =
  'flex h-10 w-full rounded-lg border border-input bg-background px-3 py-1.5 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

function documentName(path: string): string {
  try {
    const url = new URL(path);
    const privatePath = url.searchParams.get('path') ?? url.pathname;
    return privatePath.split('/').pop() ?? 'document';
  } catch {
    return path.split('/').pop() ?? path;
  }
}

function EditFieldsPanel({
  fields,
  onChange,
}: {
  fields: ClaimResubmitFieldUpdates;
  onChange: (fields: ClaimResubmitFieldUpdates) => void;
}) {
  return (
    <div className="space-y-2 rounded-lg border border-border bg-muted/30 p-3">
      <label className="block">
        <span className="mb-1 block text-xs text-muted-foreground">Purpose</span>
        <input className={fieldClass} value={fields.title ?? ''} onChange={(e) => onChange({ ...fields, title: e.target.value })} />
      </label>
      <label className="block">
        <span className="mb-1 block text-xs text-muted-foreground">Amount (RM)</span>
        <input type="number" step="0.01" min="0" className={fieldClass} value={fields.amount ?? 0} onChange={(e) => onChange({ ...fields, amount: parseFloat(e.target.value) || 0 })} />
      </label>
      <label className="block">
        <span className="mb-1 block text-xs text-muted-foreground">Additional details</span>
        <textarea rows={2} className={fieldClass} value={fields.additionalDetails ?? ''} onChange={(e) => onChange({ ...fields, additionalDetails: e.target.value })} />
      </label>
      <label className="block">
        <span className="mb-1 block text-xs text-muted-foreground">Bank payable to</span>
        <input className={fieldClass} value={fields.bankPayableTo ?? ''} onChange={(e) => onChange({ ...fields, bankPayableTo: e.target.value })} />
      </label>
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="mb-1 block text-xs text-muted-foreground">Bank name</span>
          <input className={fieldClass} value={fields.bankName ?? ''} onChange={(e) => onChange({ ...fields, bankName: e.target.value })} />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs text-muted-foreground">Account no.</span>
          <input className={fieldClass} value={fields.bankAccountNo ?? ''} onChange={(e) => onChange({ ...fields, bankAccountNo: e.target.value })} />
        </label>
      </div>
    </div>
  );
}

type ViewTab = 'mine' | 'team';
type StatusFilter = 'ALL' | 'DRAFT' | 'PENDING' | 'DOCS_REQUESTED' | 'SUBMITTED' | 'APPROVED' | 'REJECTED';

export default function ClaimsList() {
  const { user } = useAuth();
  const isApprover = user ? canApproveClaims(user.role) : false;
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get('highlight');
  const highlightRef = useRef<HTMLLIElement>(null);

  const [viewTab, setViewTab] = useState<ViewTab>(isApprover ? 'team' : 'mine');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(isApprover ? 'PENDING' : 'ALL');
  const [list, setList] = useState<ClaimItem[]>([]);
  const [stats, setStats] = useState<{ pendingCount: number; totalAmount: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionId, setActionId] = useState<string | null>(null);
  const [rejectModalId, setRejectModalId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectLoading, setRejectLoading] = useState(false);

  // Docs upload state
  const [uploadingForId, setUploadingForId] = useState<string | null>(null);
  const [scannerForId, setScannerForId] = useState<string | null>(null);
  const [pendingFiles, setPendingFiles] = useState<{ name: string; path: string }[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadClaimIdRef = useRef<string | null>(null);
  // Preview-before-attach for scanner
  const [pendingScan, setPendingScan] = useState<{ url: string; claimId: string } | null>(null);
  const [attachingScan, setAttachingScan] = useState(false);

  // Edit-fields-on-resubmit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editFields, setEditFields] = useState<ClaimResubmitFieldUpdates>({});
  // Claim rendered into the hidden print area (Print-to-PDF on every card).
  const [printClaim, setPrintClaim] = useState<ClaimItem | null>(null);

  const handlePrint = (claim: ClaimItem) => {
    setPrintClaim(claim);
    // Let React paint the print area before opening the print dialog.
    window.setTimeout(() => window.print(), 80);
  };

  const load = useCallback(async (tab: ViewTab, status: StatusFilter) => {
    setLoading(true);
    setError('');
    const statusParam = status === 'ALL' ? undefined : status;
    const res = await getClaims({ scope: tab === 'team' ? 'team' : 'mine', status: statusParam, per_page: 50 });
    if (res.data?.data) setList(res.data.data);
    else if (res.error) { setError(res.error); setList([]); }
    else setList([]);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (viewTab === 'mine') {
      getClaimsStats().then((r) => { if (r.data?.data) setStats(r.data.data); });
    }
  }, [viewTab]);

  useEffect(() => { load(viewTab, statusFilter); }, [viewTab, statusFilter, load]);

  useEffect(() => {
    if (!highlightId || loading) return;
    setTimeout(() => highlightRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
  }, [highlightId, loading, list]);

  const handleApprove = async (id: string) => {
    setActionId(id);
    const res = await approveClaim(id);
    setActionId(null);
    if (res.error) { setError(res.error); return; }
    await load(viewTab, statusFilter);
  };

  const handleReject = (id: string) => {
    setRejectReason('');
    setRejectModalId(id);
  };

  const handleRejectConfirm = async () => {
    if (!rejectModalId || !rejectReason.trim()) return;
    setRejectLoading(true);
    const res = await rejectClaim(rejectModalId, rejectReason.trim());
    setRejectLoading(false);
    if (res.error) { setError(res.error); return; }
    setRejectModalId(null);
    setRejectReason('');
    await load(viewTab, statusFilter);
  };

  const handleSubmit = async (id: string) => {
    setActionId(id);
    const res = await submitClaim(id);
    setActionId(null);
    if (res.error) { setError(res.error); return; }
    await load(viewTab, statusFilter);
  };

  // File upload for docs-requested
  const openFilePickerForClaim = (id: string) => {
    uploadClaimIdRef.current = id;
    setPendingFiles([]);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    const id = uploadClaimIdRef.current;
    if (!id) return;
    setUploadingForId(id);
    const uploaded: { name: string; path: string }[] = [];
    for (const file of files) {
      try {
        const dataUrl = await readFileAsDataUrl(file);
        const { path } = await uploadScanFile(dataUrl);
        uploaded.push({ name: file.name, path });
      } catch { setError('Failed to upload a file. Please try again.'); }
    }
    setPendingFiles((prev) => [...prev, ...uploaded]);
    setUploadingForId(null);
    if (e.target) e.target.value = '';
  };

  // Scanner capture → show preview first, don't upload yet
  const handleScanCapture = (dataUrl: string) => {
    const id = scannerForId;
    setScannerForId(null);
    if (!id) return;
    setPendingScan({ url: dataUrl, claimId: id });
  };

  const handleAttachScan = async () => {
    if (!pendingScan) return;
    setAttachingScan(true);
    setUploadingForId(pendingScan.claimId);
    try {
      const { path } = await uploadScanFile(pendingScan.url);
      setPendingFiles((prev) => [...prev, { name: `scan_${Date.now()}.jpg`, path }]);
      setPendingScan(null);
    } catch { setError('Failed to upload scanned image.'); }
    setAttachingScan(false);
    setUploadingForId(null);
  };

  const handleDiscardScan = () => setPendingScan(null);

  const handleResubmit = async (id: string) => {
    setActionId(id);
    const fields = editingId === id ? editFields : undefined;
    const res = await resubmitClaim(id, pendingFiles.map((f) => f.path), fields);
    setActionId(null);
    if (res.error) { setError(res.error); return; }
    setPendingFiles([]);
    uploadClaimIdRef.current = null;
    setEditingId(null);
    setEditFields({});
    await load(viewTab, statusFilter);
  };

  const toggleEditFields = (c: ClaimItem) => {
    if (editingId === c.id) {
      setEditingId(null);
      setEditFields({});
      return;
    }
    setEditingId(c.id);
    setEditFields({
      title: c.title ?? '',
      amount: c.amount ?? 0,
      additionalDetails: c.additionalDetails ?? '',
      bankPayableTo: c.bankPayableTo ?? '',
      bankName: c.bankName ?? '',
      bankAccountNo: c.bankAccountNo ?? '',
    });
  };

  const myStatusFilters: StatusFilter[] = ['ALL', 'DRAFT', 'PENDING', 'SUBMITTED', 'DOCS_REQUESTED', 'APPROVED', 'REJECTED'];
  const teamStatusFilters: StatusFilter[] = ['ALL', 'PENDING', 'SUBMITTED', 'DOCS_REQUESTED', 'APPROVED', 'REJECTED'];

  return (
    <>
      {rejectModalId && (
        <div className="fixed inset-0 z-[150] flex items-end justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-t-2xl bg-background p-5 shadow-2xl space-y-4">
            <h3 className="text-base font-semibold">Reject claim</h3>
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium">Reason (required)</span>
              <textarea
                rows={3}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter rejection reason…"
                className="flex w-full rounded-xl border border-input bg-background px-4 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                autoFocus
              />
            </label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setRejectModalId(null)}
                disabled={rejectLoading}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                className="flex-1"
                onClick={handleRejectConfirm}
                disabled={rejectLoading || !rejectReason.trim()}
              >
                {rejectLoading ? 'Rejecting…' : 'Reject'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {scannerForId && (
        <DocumentScanner
          onCapture={handleScanCapture}
          onClose={() => setScannerForId(null)}
        />
      )}

      {/* Preview before attaching the scanned image */}
      {pendingScan && (
        <div className="fixed inset-0 z-[150] flex flex-col justify-end bg-black/60 backdrop-blur-sm">
          <div className="rounded-t-2xl bg-background overflow-hidden shadow-2xl">
            <div className="flex items-center justify-center bg-foreground" style={{ minHeight: '50vh' }}>
              <img
                src={pendingScan.url}
                alt="Scanned document preview"
                className="max-h-[50vh] max-w-full object-contain"
              />
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm font-semibold text-center">Attach this scan?</p>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={handleDiscardScan}
                  disabled={attachingScan}
                >
                  Discard
                </Button>
                <Button
                  type="button"
                  className="flex-1"
                  onClick={handleAttachScan}
                  disabled={attachingScan}
                >
                  {attachingScan ? 'Uploading…' : 'Attach scan'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <input ref={fileInputRef} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={handleFileChange} />

      <div className="space-y-6">
        <PageHeader
          title={viewTab === 'team' ? 'Team claims' : 'My claims'}
          subtitle={viewTab === 'team' ? 'Review and approve team expenses' : 'Expenses and reimbursements'}
          actions={
            viewTab === 'mine' ? (
              <Button size="sm" asChild><Link to="/claims/new">New claim</Link></Button>
            ) : undefined
          }
        />

        {isApprover && (
          <div className="flex gap-2 rounded-2xl border border-border bg-muted/30 p-1">
            {(['mine', 'team'] as ViewTab[]).map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => { setViewTab(tab); setStatusFilter('ALL'); }}
                className={cn(
                  'flex-1 rounded-xl px-3 py-2 text-sm font-medium transition-all',
                  viewTab === tab ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {tab === 'mine' ? 'My claims' : 'Team claims'}
              </button>
            ))}
          </div>
        )}

        {/* Status filters */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {(viewTab === 'mine' ? myStatusFilters : teamStatusFilters).map((status) => (
            <button
              key={status}
              type="button"
              onClick={() => setStatusFilter(status)}
              className={cn(
                'shrink-0 rounded-full px-4 py-2 text-sm font-medium transition-all',
                statusFilter === status
                  ? 'bg-primary text-primary-foreground'
                  : 'border border-border bg-card text-muted-foreground hover:bg-muted'
              )}
            >
              {status === 'ALL' ? 'All' : (CLAIM_STATUS_LABELS[status as ClaimStatus] ?? status)}
            </button>
          ))}
        </div>

        {error && (
          <div className="rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {loading ? (
          <LoadingState label="Loading claims…" />
        ) : (
          <>
            {viewTab === 'mine' && stats !== null && (
              <div className="grid grid-cols-2 gap-3">
                <MetricCard icon={Receipt} value={stats.pendingCount} title="Pending" hint="Awaiting approval" />
                <MetricCard icon={Receipt} value={formatMoney(stats.totalAmount)} title="Total" hint="All claims" />
              </div>
            )}

            {list.length === 0 ? (
              <EmptyState
                icon={Receipt}
                title={viewTab === 'team' ? 'No team claims' : 'No claims yet'}
                description={viewTab === 'team' ? 'Team claims will appear here.' : 'Submit your first expense or reimbursement claim.'}
                action={viewTab === 'mine' ? <Button asChild><Link to="/claims/new">New claim</Link></Button> : undefined}
              />
            ) : (
              <ul className="space-y-3">
                {list.map((c) => {
                  const isDocsRequested = c.status === 'DOCS_REQUESTED';
                  const isRejected = c.status === 'REJECTED';
                  const isDraft = c.status === 'DRAFT';
                  const isUploadingThis = uploadingForId === c.id;
                  const isUploadTarget = uploadClaimIdRef.current === c.id;
                  const canTeamAct = viewTab === 'team' && c.status === 'PENDING';

                  return (
                    <li key={c.id} ref={c.id === highlightId ? highlightRef : undefined}>
                      <Card className={cn(c.id === highlightId && 'ring-2 ring-primary ring-offset-2')}>
                        <CardContent className="p-4 space-y-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              {viewTab === 'team' && c.employee?.fullName && (
                                <p className="text-sm font-medium text-primary">{c.employee.fullName}</p>
                              )}
                              <p className="font-semibold">{c.title ?? c.claimTypeLabel ?? c.claimType}</p>
                              <p className="mt-1 text-sm font-semibold tabular-nums">{formatMoney(c.amount ?? 0)}</p>
                              <p className="text-xs text-muted-foreground">{formatDate(c.claimDate ?? c.createdAt)}</p>
                            </div>
                            <Badge variant={CLAIM_STATUS_VARIANT[c.status as ClaimStatus] ?? 'secondary'}>
                              {CLAIM_STATUS_LABELS[c.status as ClaimStatus] ?? c.status}
                            </Badge>
                            <button
                              type="button"
                              onClick={() => handlePrint(c)}
                              title="Print to PDF"
                              className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground print:hidden"
                            >
                              <Printer className="h-4 w-4" />
                            </button>
                          </div>

                          {c.rejectionReason && (
                            <p className="border-t border-border pt-3 text-sm text-destructive/90">{c.rejectionReason}</p>
                          )}

                          {/* Finance note when docs requested */}
                          {isDocsRequested && c.docsRequestedNote && (
                            <div className="rounded-lg border border-accent/20 bg-accent/10 p-3 text-sm text-accent">
                              <p className="font-medium mb-1">Finance note</p>
                              <p>{c.docsRequestedNote}</p>
                            </div>
                          )}

                          {/* Per-claim supporting documents */}
                          {(c.supportingFiles?.length ?? 0) > 0 && (
                            <div className="border-t border-border pt-3">
                              <p className="mb-2 text-xs font-medium text-muted-foreground">
                                Attached documents ({c.supportingFiles!.length})
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {c.supportingFiles!.map((path, i) => {
                                  const url = storageUrl(path);
                                  const name = documentName(path);
                                  return isImagePath(path) ? (
                                    <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                                      <img
                                        src={url}
                                        alt={name}
                                        className="h-16 w-16 rounded-md border border-border object-cover"
                                      />
                                    </a>
                                  ) : (
                                    <a
                                      key={i}
                                      href={url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex h-16 w-16 flex-col items-center justify-center gap-1 rounded-md border border-border bg-muted p-1 text-center"
                                    >
                                      <Paperclip className="h-5 w-5 text-muted-foreground" />
                                      <span className="line-clamp-2 text-[10px] text-muted-foreground">{name}</span>
                                    </a>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Employee: submit draft */}
                          {isDraft && viewTab === 'mine' && (
                            <div className="flex gap-2 border-t border-border pt-3">
                              <Button size="sm" disabled={actionId === c.id} onClick={() => handleSubmit(c.id)}>
                                {actionId === c.id ? 'Submitting…' : 'Submit for approval'}
                              </Button>
                            </div>
                          )}

                          {/* Employee: upload docs for docs-requested */}
                          {isDocsRequested && viewTab === 'mine' && (
                            <div className="border-t border-border pt-3 space-y-3">
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => openFilePickerForClaim(c.id)} disabled={isUploadingThis}>
                                  <Paperclip className="mr-1.5 h-3.5 w-3.5" />
                                  {isUploadingThis ? 'Uploading…' : 'Attach files'}
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => { uploadClaimIdRef.current = c.id; setPendingFiles([]); setScannerForId(c.id); }} disabled={isUploadingThis}>
                                  <Camera className="mr-1.5 h-3.5 w-3.5" />
                                  Scan
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => toggleEditFields(c)}>
                                  {editingId === c.id ? 'Cancel edit' : 'Edit details'}
                                </Button>
                              </div>

                              {editingId === c.id && (
                                <EditFieldsPanel fields={editFields} onChange={setEditFields} />
                              )}

                              {isUploadTarget && pendingFiles.length > 0 && (
                                <ul className="space-y-1">
                                  {pendingFiles.map((f, i) => (
                                    <li key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                                      <Paperclip className="h-3 w-3" />
                                      <span className="flex-1 truncate">{f.name}</span>
                                      <button type="button" onClick={() => setPendingFiles((prev) => prev.filter((_, j) => j !== i))}>
                                        <X className="h-3 w-3 hover:text-destructive" />
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              )}

                              {isUploadTarget && (
                                <Button size="sm" disabled={actionId === c.id} onClick={() => handleResubmit(c.id)}>
                                  {actionId === c.id
                                    ? 'Resubmitting…'
                                    : pendingFiles.length
                                      ? 'Upload & resubmit'
                                      : 'Resubmit'}
                                </Button>
                              )}
                            </div>
                          )}

                          {/* Employee: resubmit rejected claim */}
                          {isRejected && viewTab === 'mine' && (
                            <div className="border-t border-border pt-3 space-y-2">
                              <p className="text-xs text-muted-foreground">You can resubmit this claim to restart the approval process.</p>
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => toggleEditFields(c)}>
                                  {editingId === c.id ? 'Cancel edit' : 'Edit details'}
                                </Button>
                                <Button size="sm" disabled={actionId === c.id} onClick={() => handleResubmit(c.id)}>
                                  {actionId === c.id ? 'Resubmitting…' : 'Resubmit claim'}
                                </Button>
                              </div>
                              {editingId === c.id && (
                                <EditFieldsPanel fields={editFields} onChange={setEditFields} />
                              )}
                            </div>
                          )}

                          {/* Team approver actions */}
                          {canTeamAct && (
                            <div className="flex gap-2 border-t border-border pt-3">
                              <Button size="sm" disabled={actionId === c.id} onClick={() => handleApprove(c.id)}>Approve</Button>
                              <Button size="sm" variant="outline" disabled={actionId === c.id} onClick={() => handleReject(c.id)}>Reject</Button>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </div>

      {/* Print-to-PDF document (hidden on screen, visible in print) */}
      {printClaim && (
        <div id="claim-print-area" className="hidden print:block">
          <div className="mx-auto max-w-2xl p-6 text-sm text-gray-900">
            <div className="flex items-start justify-between border-b border-gray-300 pb-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Claim form
                </p>
                <h2 className="mt-1 text-lg font-bold">
                  {printClaim.title ?? printClaim.claimTypeLabel ?? printClaim.claimType}
                </h2>
                <p className="mt-0.5 text-xs text-gray-600">
                  {printClaim.claimTypeLabel ?? printClaim.claimType}
                  {printClaim.referenceNumber ? ` · ${printClaim.referenceNumber}` : ''}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">Amount</p>
                <p className="text-xl font-bold">{formatMoney(printClaim.amount ?? 0)}</p>
              </div>
            </div>

            <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 text-xs">
              <div>
                <dt className="text-gray-500">Employee</dt>
                <dd className="font-medium">{printClaim.employee?.fullName ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Date</dt>
                <dd className="font-medium">{formatDate(printClaim.claimDate)}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Status</dt>
                <dd className="font-medium">
                  {CLAIM_STATUS_LABELS[printClaim.status as ClaimStatus] ?? printClaim.status}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Submitted</dt>
                <dd className="font-medium">{formatDate(printClaim.createdAt)}</dd>
              </div>
            </dl>

            {printClaim.description && (
              <p className="mt-4 border-t border-gray-200 pt-3 text-xs text-gray-700">
                {printClaim.description}
              </p>
            )}
            {printClaim.additionalDetails && (
              <p className="mt-2 text-xs text-gray-700">{printClaim.additionalDetails}</p>
            )}
            {printClaim.purchasePurpose && (
              <p className="mt-2 text-xs text-gray-700">
                Purchase purpose: {printClaim.purchasePurpose}
              </p>
            )}

            {printClaim.items && printClaim.items.length > 0 && (
              <table className="mt-4 w-full border-collapse text-xs">
                <thead>
                  <tr className="border-b border-gray-300 text-left text-gray-500">
                    <th className="py-1 pr-2">Description</th>
                    <th className="py-1 pr-2 text-right">Qty</th>
                    <th className="py-1 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {printClaim.items.map((item, i) => (
                    <tr key={i} className="border-b border-gray-100">
                      <td className="py-1 pr-2">{item.description}</td>
                      <td className="py-1 pr-2 text-right">{item.quantity ?? 1}</td>
                      <td className="py-1 text-right">{formatMoney(item.amount * (item.quantity ?? 1))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {printClaim.rejectionReason && (
              <p className="mt-4 border-t border-gray-200 pt-3 text-xs text-red-700">
                Rejection reason: {printClaim.rejectionReason}
              </p>
            )}
            {printClaim.docsRequestedNote && (
              <p className="mt-2 text-xs text-gray-700">
                Finance note: {printClaim.docsRequestedNote}
              </p>
            )}

            <p className="mt-8 text-center text-[10px] text-gray-400">
              Computer-generated claim form
            </p>
          </div>
        </div>
      )}
    </>
  );
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
